import { createFileRoute, Link } from '@tanstack/react-router';

import { SettingsScreen } from '../../features/settings/settings-screen';
import { RequirePermission } from '../../lib/session/guards';
import { ADMIN_PERMISSIONS } from '../../lib/session/permissions';

export const Route = createFileRoute('/settings/')({
  component: SettingsPage,
});

/**
 * `/settings` — the clinic's details, look, policies and alerts (MASTER_PLAN M11.4,
 * FR-ADMIN-06, FR-PLAT-03).
 *
 * The route's whole job is the permission guard and the four links, so the screen itself
 * stays router-free and therefore testable without a route tree (rules §17.8).
 *
 * `tenant.settings.manage` is the key that opens the page. The individual sections are
 * `tenant.branding.manage`, `booking_settings.manage` and `notification_settings.manage`
 * on the server, and `clinic_admin` holds all four — so one guard here and the server's
 * own checks behind each save is the honest arrangement rather than four nested guards
 * around a single screen (rules §17.2, §22.2).
 */
function SettingsPage() {
  return (
    <RequirePermission keys={[ADMIN_PERMISSIONS.settingsManage]}>
      <SettingsScreen
        links={{
          scheduling: (
            <Link to="/scheduling" className="text-primary-accessible underline underline-offset-4">
              Scheduling
            </Link>
          ),
          integrations: (
            <Link
              to="/integrations"
              className="text-primary-accessible underline underline-offset-4"
            >
              Integrations
            </Link>
          ),
          patients: (
            <Link to="/patients" className="text-primary-accessible underline underline-offset-4">
              Patients
            </Link>
          ),
          domains: (
            /* This one is the page header's action, not a word in a sentence, so it
               carries a real target: 24px, and 44px on the touch layouts (M11.11). */
            <Link
              to="/settings/domains"
              className="inline-flex min-h-6 items-center text-primary-accessible underline underline-offset-4 max-lg:min-h-11"
            >
              Domains
            </Link>
          ),
        }}
      />
    </RequirePermission>
  );
}
